import constant from "./constant";
import { formatUserFacingError, MSG } from "./constants/userMessages.js";
import { clearOfflineQueue } from "./utils/offlineQueue";

export const removeStorage = (keyName) => {
  localStorage.removeItem(keyName);
}

export const setStorage = (key, jsonObj) => {
  localStorage.setItem(key, JSON.stringify(jsonObj))
}


export const getStorage = (keyName) => {
  const item = localStorage.getItem(keyName);
  if (item == null) return null;
  try {
    return JSON.parse(item);
  } catch {
    localStorage.removeItem(keyName);
    return null;
  }
}

export const removeAllLocalStorage = () => {
  localStorage.clear();
}

export const removeSessionStorage = (keyName) => {
  sessionStorage.removeItem(keyName);
}

export const setSessionStorage = (key, jsonObj) => {
  sessionStorage.setItem(key, JSON.stringify(jsonObj));
}

export const getSessionStorage = (keyName) => {
  const item = sessionStorage.getItem(keyName);
  if (item == null) return null;
  try {
    return JSON.parse(item);
  } catch {
    sessionStorage.removeItem(keyName);
    return null;
  }
}

export const removeAllSessionStorage = () => {
  sessionStorage.clear();
}

export const validateFunctionalForm = (state, setState) => {
  let status = true;

  const validateField = (field) => {
    if (!field?.isRequired) return { ...field, error: "" };

    const value = field.value;

    if (value === undefined || value === null) {
      status = false;
      return { ...field, error: "This field is required" };
    }

    if (typeof value === "string" && value.trim() === "") {
      status = false;
      return { ...field, error: "This field is required" };
    }

    if (Array.isArray(value) && value.length === 0) {
      status = false;
      return { ...field, error: "This field is required" };
    }

    return { ...field, error: "" };
  };

  const validateObject = (obj) => {
    const updated = { ...obj };

    Object.keys(updated).forEach((key) => {
      const field = updated[key];

      // 🔥 nested array (units)
      if (Array.isArray(field)) {
        updated[key] = field.map((item) => validateObject(item));
      }

      // 🔥 normal field
      else if (field && typeof field === "object" && "value" in field) {
        updated[key] = validateField(field);
      }

      // 🔥 nested object
      else if (typeof field === "object") {
        updated[key] = validateObject(field);
      }
    });

    return updated;
  };

  let updatedState;

  // ✅ Case 1: Array
  if (Array.isArray(state)) {
    updatedState = state.map((item) => validateObject(item));
  }
  // ✅ Case 2: Object
  else {
    updatedState = validateObject(state);
  }

  setState(updatedState);
  return status;
};



export const getAuthHeader = () => {
  const config = { withCredentials: true, headers: {} };
  try {
    const token = getStorage(constant.keys.userToken);
    if (token) {
      config.headers.Authorization = "Bearer " + token;
    }
  } catch {
    // HttpOnly cookie carries the session token
  }
  return config;
};

/** fetch() init — axios `withCredentials` is ignored; use credentials: "include". */
export const getFetchAuthInit = (init = {}) => {
  const auth = getAuthHeader();
  return {
    ...init,
    credentials: "include",
    headers: { ...(auth.headers || {}), ...(init.headers || {}) },
  };
};

export const getUserFacingError = (err, fallback = MSG.requestFailed) =>
  formatUserFacingError(
    err?.response?.data?.error?.message ||
      err?.response?.data?.error ||
      err?.message,
    fallback
  );

export const userFacingError = getUserFacingError;

export const parseApiErrorMessage = (body, fallback = MSG.requestFailed) => {
  if (!body) return formatUserFacingError(fallback, fallback);
  let message = fallback;
  if (typeof body.error === "string") message = body.error;
  else if (body.error?.message) message = body.error.message;
  else if (typeof body.message === "string") message = body.message;
  else if (body.message && typeof body.message === "object") {
    message = body.message.message || fallback;
  }
  return formatUserFacingError(message, fallback);
};

/** Normalize API `{ error: { message } }` / string / Error into a toast-safe string. */
export const apiErrorMessage = (error, fallback = MSG.requestFailed) => {
  if (!error) return fallback;
  if (typeof error === "string") return formatUserFacingError(error, fallback);
  if (error instanceof Error) {
    return formatUserFacingError(error.message, fallback);
  }
  if (typeof error?.message === "string") {
    return formatUserFacingError(error.message, fallback);
  }
  return parseApiErrorMessage(error, fallback);
};

/** Prevent axios rejections from surfacing as uncaught runtime errors on read calls. */
export const catchApi = (promise) =>
  promise.catch((error) => ({
    status: false,
    allowed: false,
    error: {
      message: parseApiErrorMessage(
        error?.response?.data,
        error?.message || MSG.requestFailed
      ),
    },
  }));

/** Parse JSON error body returned as blob from axios responseType: "blob". */
export const parseBlobDataError = async (data, fallback = MSG.requestFailed) => {
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return parseApiErrorMessage(json, fallback);
    } catch {
      return fallback;
    }
  }
  if (typeof data === "string") {
    try {
      return parseApiErrorMessage(JSON.parse(data), fallback);
    } catch {
      return fallback;
    }
  }
  return parseApiErrorMessage(data, fallback);
};

export const parseAxiosBlobError = async (error, fallback = MSG.requestFailed) =>
  parseBlobDataError(error?.response?.data, fallback);

/** Ensure a blob axios response is a real PDF (guards soft-resolved 4xx / wrong payloads). */
export const assertPdfBlobResponse = async (response, fallback = MSG.requestFailed) => {
  const data = response?.data;
  const status = response?.status;
  if (status >= 400 || status === 0) {
    throw new Error(await parseBlobDataError(data, fallback));
  }
  if (!(data instanceof Blob) && !(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) {
    throw new Error(fallback);
  }
  const slice =
    data instanceof Blob
      ? await data.slice(0, 5).arrayBuffer()
      : data instanceof ArrayBuffer
        ? data.slice(0, 5)
        : data.buffer.slice(data.byteOffset, data.byteOffset + 5);
  const magic = String.fromCharCode(...new Uint8Array(slice));
  if (!magic.startsWith("%PDF")) {
    throw new Error(await parseBlobDataError(data, fallback));
  }
  return data;
};

/** Open a PDF blob in a new tab (avoid noopener — Chrome fails blob: PDFs with it). */
export const openPdfBlob = (blob, fileName = "document.pdf") => {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, "_blank");
  if (!opened) {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  return url;
};

export const setAuthSession = () => {
  sessionStorage.setItem(constant.keys.authSession, "1");
  removeStorage(constant.keys.userToken);
}

export const clearAuthSession = () => {
  sessionStorage.removeItem(constant.keys.authSession);
  removeStorage(constant.keys.userToken);
  if (typeof indexedDB !== "undefined") {
    indexedDB.deleteDatabase("elog_read_cache_v1");
    clearOfflineQueue().catch(() => {
      indexedDB.deleteDatabase("elog_offline_v1");
    });
  }
}

export const hasAuthSession = () =>
  sessionStorage.getItem(constant.keys.authSession) === "1";



export const formatVoyageNumber = (voyage) => {
  if (!voyage) return "";

  return voyage.voyage_number;
};



/* ================= HELPERS ================= */
export const formatDateTime = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const yesNo = (val) => (val ? "Yes" : "No");
